/* eslint-disable max-lines */
import PlannerPresenter from 'hooks/PlannerPresenter';
import { useEffect, useMemo, useState } from 'react';
import { LayerGroup, Polyline, useMap, useMapEvents } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  plannerSegmentsInformation,
  plannerSegmentsSlice,
} from 'redux/slices/plannerSegmentsSlice';
import { plannerInformation } from 'redux/slices/plannerSlice';
import { RootState } from 'redux/store';
import { R } from 'resources/R';
import { ReactComponent as Ic_Location_blue } from 'resources/icons/Ic_Location_blue.svg';
import Ic_Bus from 'resources/icons/Ic_Bus_white.svg';
import { ReactComponent as IcMarcador } from 'resources/icons/Ic_Marcador.svg';
import { ReactComponent as Ic_Flag } from 'resources/icons/Ic_Red_Flag.svg';
import 'resources/styles/components/widgets/map.scss';
import RouteUtils from 'utils/RouteUtils';
import {
  mapState,
  mapTooltipModalState,
  updateBounds,
  updateZoom,
} from '../../../redux/slices/mapSlice';
import styled from 'styled-components';
import LeafletMaker from './LeafletMaker';
import MarkerL from './MarkerL';
import SvgIcon from '../icons/SvgIcon';
import { useLanguage, useTranslate } from 'context/languageContext';
import IconPresenter from 'hooks/IconPresenter';
import { agencyState } from 'redux/slices/agencysSlice';
import { RUTAS_GENERALES_MUL2 } from 'utils/constants';
import { currentIncludesPath } from 'utils/utils';
import TooltipModal from './TooltipModal';
import { useNominatim } from 'hooks/useNominatim/useNominatim';
import OverlappingLinesContent from 'components/features/lines/OverlappingLinesContent';

const CustomMarker = styled(MarkerL)`
  z-index: 9999 !important;
`;

export default function LeafletMap(props: any) {
  const map = useMap();
  const { getIcon } = IconPresenter();
  const dispatch = useDispatch();
  const selectorMap = useSelector(mapState);
  const t = useTranslate();
  const selectorPlanner = useSelector(plannerInformation) as any;
  const planInfo = useSelector(plannerInformation).plannerResult as any;
  const plannerSegments = useSelector(plannerSegmentsInformation);
  const selectorPermission = useSelector((state: RootState) => state.permission);
  const agencyInfo = useSelector(agencyState);
  const showTooltipOverlapping = useSelector(mapTooltipModalState);
  const location = useLocation();
  const currentLocation = location.pathname;
  const [renderPlannerSegments, setRenderPlannerSegments] = useState(null) as any;
  const [renderVehiclesLine, setRenderVehiclesLine] = useState(null) as any;
  const dataOrigin = agencyInfo.dataOrigin;
  const currentLanguage = useLanguage();
  const { onPlannerSegmentChange } = PlannerPresenter();
  const COORDS = RouteUtils.getItiCoords(
    selectorPlanner.plannerResult,
    selectorPlanner.selectedPlan,
  ) as any;
  const { getNominatimReverse } = useNominatim();

  useMapEvents({
    move() {
      localStorage.setItem(
        'mapData',
        JSON.stringify({ coords: map.getCenter(), zoom: map.getZoom() }),
      );
    },
    zoomstart() {
      if (document.getElementsByTagName('canvas').length > 0) {
        document.getElementsByTagName('canvas')[0].style.display = 'none';
      }
    },
    zoomend() {
      dispatch(updateZoom(map.getZoom()));
      dispatch(
        updateBounds({
          south: map.getBounds().getSouth(),
          north: map.getBounds().getNorth(),
          west: map.getBounds().getWest(),
          east: map.getBounds().getEast(),
        }),
      );
      if (document.getElementsByTagName('canvas').length > 0) {
        document.getElementsByTagName('canvas')[0].style.display = '';
      }
      localStorage.setItem(
        'mapData',
        JSON.stringify({ coords: map.getCenter(), zoom: map.getZoom() }),
      );
    },

    dragend() {
      dispatch(
        updateBounds({
          south: map.getBounds().getSouth(),
          north: map.getBounds().getNorth(),
          west: map.getBounds().getWest(),
          east: map.getBounds().getEast(),
        }),
      );
    },
    load() {
      dispatch(
        updateBounds({
          south: map.getBounds().getSouth(),
          north: map.getBounds().getNorth(),
          west: map.getBounds().getWest(),
          east: map.getBounds().getEast(),
        }),
      );
    },

    click(e) {
      if (currentIncludesPath(currentLocation, RUTAS_GENERALES_MUL2.planner[currentLanguage])) {
       getNominatimReverse({lat: e.latlng.lat, lng: e.latlng.lng, planner: true});
      }
      props.onClick?.(e);
    },
  });

  const locationMarker = useMemo(() => {
    return (
      selectorPermission.PermissionGeolocation &&
      selectorPermission.cookies &&
      selectorMap?.location?.position && (
        <LeafletMaker
          position={selectorMap.location?.position}
          content={<SvgIcon className='location-marker' Icon={IcMarcador} />}
          flyZoom={selectorMap.zoom}
        />
      )
    );
  }, [
    selectorPermission.cookies,
    selectorPermission.PermissionGeolocation,
    selectorMap.location?.position,
  ]);

  //Unsubscribe on unmount
  useEffect(() => {
    return () => {};
  }, []);

  useEffect(() => {
    if (currentIncludesPath(currentLocation, RUTAS_GENERALES_MUL2.planner[currentLanguage])) {
      onPlannerSegmentChange().then();
    }
  }, [plannerSegments]);

  useEffect(() => {
    const fetchData = async () => {
      if (selectorPlanner.plannerResult) setRenderPlannerSegments(await getRenderPlannerSegments());
      else setTimeout(async () => setRenderPlannerSegments(await getRenderPlannerSegments(), 1000));
    };
    fetchData();
  }, [selectorPlanner.plannerResult, selectorPlanner.selectedPlan, plannerSegments]);

  useEffect(() => {
    const fetchData = async () => {
      setRenderVehiclesLine(await getRenderVehiclesLine());
    };
    fetchData();
  }, [selectorMap.vehicles]);

  useEffect(() => {
    if (
      isNaN(selectorMap?.flyTo?.coords?.lng) ||
      isNaN(selectorMap?.flyTo?.coords?.lat) ||
      Object.values(selectorMap?.flyTo?.coords).includes(undefined)
    ) {
      return () => {};
    } else {
      // if()
      map.flyTo(selectorMap.flyTo?.coords, selectorMap.flyTo?.zoom ?? map.getZoom(), {
        duration: selectorMap.flyTo?.duration ?? 0.9,
      });
    }
  }, [selectorMap.flyTo]);

  useEffect(() => {
    if (
      isNaN(selectorMap?.flyTo?.coords?.lng) ||
      isNaN(selectorMap?.flyTo?.coords?.lat) ||
      Object.values(selectorMap?.flyTo?.coords).includes(undefined)
    ) {
      return () => {};
    } else {
      if (selectorMap.zoom) map.setZoom(selectorMap.zoom);
    }
  }, [selectorMap.zoom]);

  const getRenderPlannerSegments = async () => {
    let result = [] as any;
    /* MARKER INICIAL */
    if (plannerSegments[0] && plannerSegments[1]) {
      //Redibujamos el marker final con coordenadas precisas.
      if (COORDS?.length) {
        const position = COORDS[0]?.coords[0];
        result.push(
          <MarkerL //marker planner
            title={plannerSegments[0]?.name}
            draggable={false}
            divUrl={
              <div className='my-destination-point'>
                <div className='my-destination-point-card'>
                  <SvgIcon
                    ariaLabel='icono destino'
                    Icon={Ic_Location_blue}
                    style={{ width: '25px', height: '25px' }}
                  />
                  <p style={{ fontWeight: 600, fontSize: '12px' }}>
                      {t('lb_planner_origen')}
                    </p>
                </div>
              </div>
            }
            divClassname={'selected'}
            x={0}
            y={0}
            iconAnchor={[20, 40]}
            onClick={() => {}}
            position={position}
            destinationPoint={true}
          />,
        );
      }
    } else {
      result.push(
        <CustomMarker
          key='first-point'
          title={plannerSegments[0]?.name}
          draggable={true}
          onDragEnd={(e) => {
            let res = {};
            res['position'] = { lat: e.target._latlng?.lat, lng: e.target._latlng.lng };
            res['name'] = e.target._latlng?.lat.toFixed(6) + ', ' + e.target._latlng.lng.toFixed(6);
            res['desc'] = '';
            res['id'] = null;
            dispatch(
              plannerSegmentsSlice.actions.set({
                index: 0,
                overwrite: true,
                stop: res,
              }),
            );
          }}
          divUrl={
            plannerSegments[0] && plannerSegments[1] ? (
              <div className='my-destination-point'>
                <div className='my-destination-point-card'>
                  <SvgIcon
                    ariaLabel='icono destino'
                    Icon={Ic_Location_blue}
                    style={{ width: '25px', height: '25px' }}
                  />
                  <p style={{ fontWeight: 600, fontSize: '12px' }}>{t('lb_planner_origen')}</p>
                </div>
              </div>
            ) : (
              <div className='planner-markers-flag'>
                <SvgIcon
                  ariaLabel='icono destino'
                  Icon={Ic_Location_blue}
                />
                <span>{t('lb_planner_origen')}</span>
              </div>
            )
          }
          divClassname={'selected'}
          x={40}
          y={40}
          iconAnchor={[19, 30]}
          onClick={() => {}}
          position={{
            lat: parseFloat(plannerSegments[0]?.position?.lat),
            lng: parseFloat(plannerSegments[0]?.position?.lng),
          }}
        />,
      );
    }

    /* MARKER FINAL */ /* Cuando se ha indicado solo el punto final y no el origen */
    if (plannerSegments[1]) {
      if (!plannerSegments[0]) {
        result.push(
          <MarkerL
            key={`${plannerSegments[1]?.from?.lat}-${plannerSegments[1]?.from?.lon}`}
            zIndexOffset={9997}
            title={plannerSegments[1].name}
            draggable={true}
            onDragEnd={(e) => {
              let res = {};
              res['position'] = { lat: e.target._latlng.lat, lng: e.target._latlng.lng };
              res['name'] =
                e.target._latlng.lat.toFixed(6) + ', ' + e.target._latlng.lng.toFixed(6);
              res['desc'] = '';
              res['id'] = null;
              dispatch(
                plannerSegmentsSlice.actions.set({
                  index: 1,
                  overwrite: true,
                  stop: res,
                }),
              );
            }}
            divUrl={
              <div className='planner-markers-flag'>
                <SvgIcon
                  ariaLabel='icono destino'
                  Icon={Ic_Flag}
                />
                <span>{t('lb_planner_destino')}</span> {/* ok */}
              </div>
            }
            divClassname='selected'
            x={0}
            y={0}
            iconAnchor={[20, 40]}
            onClick={() => {}}
            position={{
              lat: parseFloat(plannerSegments[1]?.position?.lat),
              lng: parseFloat(plannerSegments[1]?.position?.lng),
            }}
          />,
        );
      }
    }

    for (let marker in plannerSegments as any) {
      if ('my_location' !== plannerSegments[marker]?.id)
        result.push(
          <MarkerL
            key={marker + plannerSegments[marker]?.id}
            draggable={true}
            onDragEnd={(e) => {
              let res = {};
              res['location'] = {
                latitude: e.target._latlng?.lat,
                longitude: e.target._latlng?.lng,
              };
              res['name'] =
                e.target._latlng?.lat.toFixed(6) + ', ' + e.target._latlng?.lng.toFixed(6);
              res['desc'] = '';
              res['id'] = null;
              dispatch(
                plannerSegmentsSlice.actions.set({
                  index: parseInt(marker),
                  overwrite: true,
                  stop: res,
                }),
              );
            }}
            url={R.drawables.map[marker]}
            x={40}
            y={40}
            iconAnchor={[19, 30]}
            onClick={() => {}}
            position={{
              lat: parseFloat(plannerSegments[marker]?.location?.latitude),
              lng: parseFloat(plannerSegments[marker]?.location?.longitude),
            }}
          />,
        );
    }
    if (planInfo && planInfo?.plan && planInfo?.plan?.itineraries) {
      let itinerarySelected = planInfo?.plan?.itineraries[selectorPlanner.selectedPlan];
      if (itinerarySelected?.legs?.length) {
        itinerarySelected?.legs.forEach(async (leg, i) => {
          const calculatedAgencyId =
            leg.agencyId !== null ? leg.agencyId.split(':')[1] : 'EMT-Palma';
          if (RouteUtils.isTransportPublicMode(leg.mode)) {
            let dataOriginElem = dataOrigin.find(
              (dataOri: any) => String(dataOri.gtfsAgency[0]?.id) === String(calculatedAgencyId),
            );
            let iconId = dataOriginElem?.transportModes[0]?.iconId;
            //let contentIcon = allIcons.find((element: any) => element.id === iconId);
            //let icon = await getIcon(iconId);
            //let icon = await iconPresenter.getDataOrigin(props.lineSynoptic[i].agencyId, true);
            result.push(
              <MarkerL //marker planner
                key={`${leg.from?.lat}-${leg.from?.lon}-${i}`}
                title={leg.from.name}
                draggable={false}
                divUrl={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      background: 'transparent',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 'max-content',
                        height: '35px',
                        borderRadius: '10px',
                        paddingLeft: '5px',
                        paddingRight: '5px',
                        fontWeight: '600',
                        background: '#489ACA',
                      }}
                    >
                      <img
                        alt={'iconId-' + iconId}
                        src={iconId || Ic_Bus}
                        style={{ width: '20px', height: '20px', marginRight: '3px' }}
                      />
                      <span style={{ color: 'white' }}>{leg.from.stopCode}</span>
                    </div>

                    <div
                      className={'fragment-point'}
                      style={{ outline: `solid 4px#${leg.routeColor || '000000'}` }}
                    />
                  </div>
                }
                divClassname={'selected'}
                x={40}
                y={40}
                iconAnchor={[20, 40]}
                onClick={() => {}}
                position={{
                  lat: parseFloat(leg.from?.lat.toFixed(6)),
                  lng: parseFloat(leg.from?.lon.toFixed(6)),
                }}
              />,
            );
          }
        });
        /* MARKER FINAL */
        //Redibujamos el marker final con coordenadas precisas una vez indicado origen y destino.
        if (COORDS?.length) {
          const auxCoords = COORDS[COORDS?.length - 1]?.coords;
          const position = COORDS[COORDS?.length - 1]?.coords[auxCoords?.length - 1];
          result[result.length - 1] = (
            <MarkerL //marker planner
              title={plannerSegments[1]?.name}
              draggable={false}
              divUrl={
                <div className='my-destination-point'>
                  <div className='my-destination-point-card'>
                    <SvgIcon
                      ariaLabel='icono destino'
                      Icon={Ic_Flag}
                      style={{ width: '25px', height: '25px' }}
                    />
                    <p style={{ fontWeight: 600, fontSize: '12px' }}>
                      {t('lb_planner_destino')} {/* ok */}
                    </p>
                  </div>
                </div>
              }
              divClassname={'selected'}
              x={0}
              y={0}
              iconAnchor={[20, 40]}
              onClick={() => {}}
              position={position}
              destinationPoint={true}
            />
          );
        }
      }
    }
    return <div>{result}</div>;
  };

  const renderPlannerPolyline = () => {
    let result = [] as any;
    // eslint-disable-next-line array-callback-return
    COORDS?.map((feature, index) => {
      result.push(
        <Polyline
          positions={feature.coords}
          key={index + feature.color + 'back'}
          color={'#FFFFFF'}
          weight={8}
        />,
      );
      result.push(
        <Polyline
          positions={feature.coords}
          key={index + feature.color}
          color={feature?.color !== '#FFFFFF' ? feature?.color : '#000000'}
          dashArray={feature?.mode === 'WALK' ? '5, 10' : ''}
          weight={6}
        />,
      );
    });
    return result;
  };

  const getRenderVehiclesLine = async () => {
    let res = [] as any;
    for (let element in selectorMap.vehicles) {
      let icon = await getIcon(selectorMap.vehicles[element]?.iconId);
      res.push(
        <MarkerL
          key={element}
          draggable={false}
          url={icon ?? null}
          x={40}
          y={40}
          iconAnchor={[19, 30]}
          position={{
            lat: parseFloat(selectorMap.vehicles[element].position?.lat),
            lng: parseFloat(selectorMap.vehicles[element].position?.lng),
          }}
        ></MarkerL>,
      );
    }
    return res;
  };

  return (
    <LayerGroup>
      {locationMarker}
      {currentIncludesPath(currentLocation, RUTAS_GENERALES_MUL2.planner[currentLanguage]) &&
        renderPlannerSegments}
      {currentIncludesPath(currentLocation, RUTAS_GENERALES_MUL2.planner[currentLanguage]) &&
        renderPlannerPolyline()}
      {renderVehiclesLine}
      {currentIncludesPath(currentLocation, RUTAS_GENERALES_MUL2.lines[currentLanguage]) && showTooltipOverlapping ? (
        <TooltipModal
          latitude={showTooltipOverlapping.latitude}
          longitude={showTooltipOverlapping.longitude}
          content={<OverlappingLinesContent lines={showTooltipOverlapping?.lines} />}
        />
      ) : null}
    </LayerGroup>
  );
}
